import { ClassValue } from 'clsx';
import { isEmpty } from 'lodash';
import { FileList } from '@shared/components/FileList';
import { Icon } from '@shared/components/Icon';
import { Image } from '@shared/components/Image';
import { ImageList } from '@shared/components/ImageList';
import { ImagePreviewModal } from '@shared/components/ImagePreview';
import { Text } from '@shared/components/Text';
import { ButtonView, View } from '@shared/components/View';
import { Link } from '@shared/components/link';
import { Pill } from '@shared/components/pill';
import { FileField } from '@shared/data/entity/fields/FileField';
import { grid } from '@shared/styles';
import { useBoolean } from '@shared/util/hooks';
import { getFilenameParts, isImage } from '@shared/util/media/type';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getFileText } from './util/getFileText';
import { getStackItems } from './util/getStackItems';
import styles from './FileCollectionViewField.module.css';

const IMAGE_DIMENSION = 36;
/** Slots in the compact thumbnail stack; past that, the last slot is a "+N". */
const IMAGE_STACK_SLOTS = 4;

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: FileField;
}

/**
 * Default: up to three images and the first file. Compact: overlapping thumbnails when
 * every file is an image, otherwise the first file as a pill of its type. Inline: a summary by kind.
 */
export function FileCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.fileCollectionViewField, className]}
    >
      {isInline && <InlineFile field={field} />}
      {isCompact && <CompactFile field={field} />}
      {isDefault && <DefaultFile field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineFile({ field }: { field: FileField }) {
  return <>{getFileText(field)}</>;
}

/** Overlapping thumbnails when every file is an image, otherwise the first file's type pill. */
function CompactFile({ field }: { field: FileField }) {
  if (field.files.every(isImage)) {
    return <ImageStack field={field} />;
  }
  return <FileTypePill field={field} />;
}

function DefaultFile({ field }: { field: FileField }) {
  return (
    <>
      <ImageList
        files={field.files}
        editable={false}
        maxHeight={IMAGE_DIMENSION}
        maxWidth={IMAGE_DIMENSION}
        maxImages={3}
        style={{ paddingBlock: grid(0.5) }}
        itemStyle={{ borderRadius: grid(0.5) }}
      />
      <FileList files={field.files} editable={false} maxFiles={1} showFileIcon={false} />
    </>
  );
}

/**
 * Small overlapping thumbnails: up to four, or three and a "+N" in the fourth slot. Tapping the
 * stack opens the image preview carousel at the first image.
 */
function ImageStack({ field }: { field: FileField }) {
  const { shownItems: shownFiles, moreCount } = getStackItems(field.files, IMAGE_STACK_SLOTS);
  const [isPreviewOpen, openPreview, closePreview] = useBoolean(false);
  return (
    <>
      <ButtonView row centerVertically onClick={openPreview} className={styles.images}>
        {/* Keyed by position: a field can hold the same file twice. */}
        {shownFiles.map((file, index) => (
          <Image
            key={index}
            source={{ uri: file.url }}
            alt={file.name}
            className={styles.thumbnail}
          />
        ))}
        {moreCount > 0 && (
          <View center className={[styles.thumbnail, styles.moreImages]}>
            <Text size="caption1" weight="semibold" inactive>{`+${moreCount}`}</Text>
          </View>
        )}
      </ButtonView>
      {isPreviewOpen && (
        <ImagePreviewModal images={field.files} showIndex={0} visible onClose={closePreview} />
      )}
    </>
  );
}

/** The first file as a pill of its type (a file icon and extension) that opens it, and a "+N". */
function FileTypePill({ field }: { field: FileField }) {
  const [firstFile] = field.files;
  const extension = getFileExtension(firstFile.name);
  const moreCount = field.files.length - 1;
  return (
    <View row centerVertically className={styles.file}>
      <Link href={firstFile.url} underline={false}>
        <Pill>
          <View row centerVertically gap={0.5} className={styles.fileType}>
            <Icon name="file" gridSize={3} />
            {!isEmpty(extension) && (
              <Text size="overline" weight="semibold">
                {extension}
              </Text>
            )}
          </View>
        </Pill>
      </Link>
      {moreCount > 0 && <Text weight="normal">{`+${moreCount}`}</Text>}
    </View>
  );
}

/** "PDF" for "report.pdf"; empty for a name without one. */
function getFileExtension(name: string): string {
  const [filename, extension] = getFilenameParts(name);
  if (isEmpty(filename)) {
    return '';
  }
  return extension?.toUpperCase() ?? '';
}
